'use client';

import type { ComponentPropsWithRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button, type ButtonProps } from '@/shared-ui/atoms/button/button';
import { Typography, type TypographyProps } from '@/shared-ui/atoms/typography';
import { StarFilledIcon } from '@/shared-ui/icons/star-filled';
import { StarOutlineIcon } from '@/shared-ui/icons/star-outline';

const STAR_SIZE_CLASS = {
    sm: 'size-4',
    md: 'size-5',
    lg: 'size-6',
} as const;

const STAR_GAP_CLASS = {
    tight: 'gap-[2px]',
    default: 'gap-2',
} as const;

export type StarRatingSize = keyof typeof STAR_SIZE_CLASS;
export type StarRatingGap = keyof typeof STAR_GAP_CLASS;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const formatCount = (count: number) => count.toLocaleString();

// Root -----------------------------------------------------------------------

type RootProps = ComponentPropsWithRef<'div'>;

const Root = ({ className, ref, ...props }: RootProps) => (
    <div data-slot="star-rating" ref={ref} className={cn('inline-flex items-center gap-2', className)} {...props} />
);
Root.displayName = 'StarRating.Root';

// Stars ----------------------------------------------------------------------

export type StarsProps = Omit<ComponentPropsWithRef<'div'>, 'aria-label'> & {
    /** Rating value. Will be clamped to `[0, totalStars]`. */
    rating: number;
    /** Total number of stars rendered. Defaults to 5. */
    totalStars?: number;
    /** Star icon size. `sm` = 16px, `md` = 20px, `lg` = 24px. */
    size?: StarRatingSize;
    /** Gap between stars. `tight` = 2px (per Figma), `default` = 8px. */
    starGap?: StarRatingGap;
    label?: string;
};

const Stars = ({
    rating,
    totalStars = 5,
    size = 'sm',
    starGap = 'tight',
    label,
    className,
    ref,
    ...props
}: StarsProps) => {
    const { t } = useTranslation('starRating');
    const clamped = clamp(rating, 0, totalStars);
    const ariaLabel = label ?? t('starsAriaLabel', { rating: clamped, total: totalStars });
    const sizeClass = STAR_SIZE_CLASS[size];

    return (
        <div
            data-slot="star-rating-stars"
            data-size={size}
            ref={ref}
            role="img"
            aria-label={ariaLabel}
            className={cn('inline-flex items-center', STAR_GAP_CLASS[starGap], className)}
            {...props}>
            {Array.from({ length: totalStars }, (_, i) => {
                // Round to 4dp to avoid floating-point drift (e.g. 3.7 - 3 = 0.7000000000000002).
                const fraction = Math.round(clamp(clamped - i, 0, 1) * 10_000) / 10_000;
                return (
                    <span
                        key={i}
                        data-slot="star-rating-star"
                        data-fraction={fraction}
                        className={cn('relative inline-block shrink-0', sizeClass)}>
                        <StarOutlineIcon className={cn('block size-full text-snk-keyline-grey')} />
                        <span
                            data-slot="star-rating-star-fill"
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-y-0 left-0 overflow-hidden"
                            style={{ width: `${fraction * 100}%` }}>
                            <StarFilledIcon className={cn('block text-rating', sizeClass)} />
                        </span>
                    </span>
                );
            })}
        </div>
    );
};
Stars.displayName = 'StarRating.Stars';

// Value ----------------------------------------------------------------------

export type ValueProps = Omit<TypographyProps, 'children' | 'variant' | 'weight' | 'as'> & {
    rating: number;
    count?: number;
    /** Custom formatter. Receives `rating` and (optional) `count`. */
    format?: (rating: number, count?: number) => ReactNode;
};

const defaultValueFormat = (rating: number, count?: number) =>
    count !== undefined ? `${rating} (${formatCount(count)})` : `${rating}`;

const Value = ({ rating, count, format = defaultValueFormat, className, ref, ...props }: ValueProps) => (
    <Typography
        data-slot="star-rating-value"
        variant="small"
        weight="light"
        as="span"
        ref={ref}
        className={cn(className)}
        {...props}>
        {format(rating, count)}
    </Typography>
);
Value.displayName = 'StarRating.Value';

// Count ----------------------------------------------------------------------

export type CountProps = Omit<TypographyProps, 'children' | 'variant' | 'weight' | 'as'> & {
    value: number;
    /** Custom formatter. Defaults to `(N)` with locale-formatted thousands. */
    format?: (count: number) => ReactNode;
};

const defaultCountFormat = (count: number) => `(${formatCount(count)})`;

const Count = ({ value, format = defaultCountFormat, className, ref, ...props }: CountProps) => (
    <Typography
        data-slot="star-rating-count"
        variant="small"
        weight="light"
        as="span"
        ref={ref}
        className={cn(className)}
        {...props}>
        {format(value)}
    </Typography>
);
Count.displayName = 'StarRating.Count';

// ReviewsLink ----------------------------------------------------------------

export type ReviewsLinkProps = Omit<ButtonProps, 'variant' | 'size' | 'price' | 'originalPrice'>;

const ReviewsLink = ({ className, type = 'button', children, ...props }: ReviewsLinkProps) => (
    <Button
        data-slot="star-rating-reviews-link"
        variant="tertiary"
        size="default"
        type={type}
        className={cn(className)}
        {...props}>
        {children}
    </Button>
);
ReviewsLink.displayName = 'StarRating.ReviewsLink';

// Display --------------------------------------------------------------------

export type DisplayProps = Omit<TypographyProps, 'children' | 'variant' | 'as'> & {
    value: number;
    format?: (value: number) => ReactNode;
};

const Display = ({ value, format = (v) => `${v}`, className, ref, ...props }: DisplayProps) => (
    <Typography
        data-slot="star-rating-display"
        variant="display-lg"
        as="span"
        ref={ref}
        className={cn('tracking-[0.1em]', className)}
        {...props}>
        {format(value)}
    </Typography>
);
Display.displayName = 'StarRating.Display';

// Distribution ---------------------------------------------------------------

export type DistributionRow = { stars: number; count: number };

export type DistributionProps = ComponentPropsWithRef<'ul'> & {
    rows: DistributionRow[];
    /** Denominator for bar width. Defaults to `max(rows.count)`. */
    total?: number;
    /**
     * Override callback for the per-row aria-label. Defaults to the localized
     * `starRating.distributionRowAriaLabel` string (`{{stars}} star: {{count}} reviews` in en).
     */
    label?: (row: DistributionRow) => string;
};

const Distribution = ({ rows, total, label, className, ref, ...props }: DistributionProps) => {
    const { t } = useTranslation('starRating');
    const maxCount = total ?? Math.max(1, ...rows.map((r) => r.count));
    return (
        <ul data-slot="star-rating-distribution" ref={ref} className={cn('flex flex-col gap-2', className)} {...props}>
            {rows.map((row) => {
                const fraction = clamp(row.count / maxCount, 0, 1);
                const rowLabel = label
                    ? label(row)
                    : t('distributionRowAriaLabel', {
                          stars: row.stars,
                          count: row.count,
                      });
                return (
                    <li
                        key={row.stars}
                        data-slot="star-rating-distribution-row"
                        data-stars={row.stars}
                        aria-label={rowLabel}
                        className="flex items-center gap-2">
                        <span className="inline-flex min-w-[2ch] items-center gap-1 text-xs font-medium tabular-nums">
                            <span>{row.stars}</span>
                            <StarFilledIcon className="size-3 text-rating" aria-hidden="true" />
                        </span>
                        <span
                            data-slot="star-rating-distribution-bar"
                            aria-hidden="true"
                            className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                            <span
                                data-slot="star-rating-distribution-bar-fill"
                                className="absolute inset-y-0 left-0 bg-rating"
                                style={{ width: `${fraction * 100}%` }}
                            />
                        </span>
                        <span className="min-w-[3ch] text-right text-xs font-light tabular-nums">{row.count}</span>
                    </li>
                );
            })}
        </ul>
    );
};
Distribution.displayName = 'StarRating.Distribution';

// Namespace export -----------------------------------------------------------

const StarRating = { Root, Stars, Value, Count, ReviewsLink, Display, Distribution } as const;

export { StarRating };
